import React from 'react';
import { Clock, DollarSign, Users, MapPin, CheckCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CommunityFieldIndicator } from '@/components/camps/CommunityBadge';

/**
 * Quick Facts Bar Component
 * 
 * A sticky, scannable bar showing key camp information at a glance
 * 
 * @param {Object} props
 * @param {Object} props.camp - Camp data
 */
const QuickFactsBar = ({ camp }) => {
  // Format age range
  const formatAgeRange = () => {
    if (camp.age_range) {
      return camp.age_range;
    }
    if (camp.age_min || camp.age_max) {
      return `Ages ${camp.age_min || '?'} - ${camp.age_max || '?'}`;
    }
    return 'All ages';
  };

  // Verified Icon Component
  const VerifiedIcon = () => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
        </TooltipTrigger>
        <TooltipContent>
          <p>Verified by community</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  // Quick Fact Component
  const QuickFact = ({ icon: Icon, label, value, isVerified = false, field, camp, children }) => (
    <div className="flex items-center gap-3">
      {/* Fact Icon */}
      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-brand-600" />
      </div>
      
      {/* Fact Content */}
      <div className="flex-1 min-w-0">
        <div className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">
          {label}
        </div>
        <div className="text-sm font-semibold text-gray-900 flex items-center gap-1.5 flex-wrap">
          {value || children}
          {isVerified && <VerifiedIcon />}
          {field && camp && (
            <CommunityFieldIndicator 
              field={field} 
              camp={camp}
              size="sm"
            />
          )}
        </div>
      </div>
    </div>
  );

  // Get times - try multiple sources
  const getTimes = () => {
    if (camp.user_session_start_time && camp.user_session_end_time) {
      return `${camp.user_session_start_time} - ${camp.user_session_end_time}`;
    }
    if (camp.session_start_time && camp.session_end_time) {
      return `${camp.session_start_time} - ${camp.session_end_time}`;
    }
    if (camp.schedule_dates) {
      return camp.schedule_dates;
    }
    return null;
  };

  // Get cost - try multiple sources
  const getCost = () => {
    // Try user_cost_details first
    if (camp.user_cost_details?.amount) {
      return `$${camp.user_cost_details.amount}/${camp.user_cost_details.per || 'week'}`;
    }
    // Try cost field
    if (camp.cost) {
      return `$${camp.cost}/week`;
    }
    // Try price field
    if (camp.price) {
      return `$${camp.price}/week`;
    }
    // Try cost_structure
    if (camp.cost_structure) {
      return camp.cost_structure;
    }
    return null;
  };

  const times = getTimes();
  const cost = getCost();
  const ageRange = formatAgeRange();
  const gradeRange = camp.grade_range || null;
  const distance = camp.distance;

  return (
    <div className="sticky top-0 z-10 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <div className="flex flex-wrap gap-3 sm:gap-4">
          {/* Times Fact - Card Style */}
          {times && (
            <div className="inline-flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-2.5 border border-gray-200 flex-1 min-w-[140px]">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">Times</span>
                <span className="text-sm font-semibold text-gray-900 truncate">{times}</span>
              </div>
            </div>
          )}

          {/* Cost Fact - Card Style */}
          {cost && (
            <div className="inline-flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-2.5 border border-gray-200 flex-1 min-w-[140px]">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">Cost</span>
                <span className="text-sm font-semibold text-gray-900 truncate">{cost}</span>
              </div>
            </div>
          )}

          {/* Ages Fact - Card Style */}
          <div className="inline-flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-2.5 border border-gray-200 flex-1 min-w-[140px]">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">Ages</span>
              <span className="text-sm font-semibold text-gray-900 truncate">{ageRange}</span>
            </div>
          </div>

          {/* Rising Grades Fact - Card Style (only when present) */}
          {gradeRange && (
            <div className="inline-flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-2.5 border border-gray-200 flex-1 min-w-[140px]">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">Rising grades</span>
                <span className="text-sm font-semibold text-gray-900 truncate">{gradeRange}</span>
              </div>
            </div>
          )}

          {/* Distance Fact - Card Style */}
          {distance !== undefined && distance !== null && (
            <div className="inline-flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-2.5 border border-gray-200 flex-1 min-w-[140px]">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">Distance</span>
                <span className="text-sm font-semibold text-gray-900 truncate">{distance} mi</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickFactsBar;
